import { useCallback, useEffect, useState, type MouseEvent } from 'react';
import {
  addToCart,
  getCartItems,
  removeFromCart,
  toggleFavorite,
  toggleLike,
  type CartItem,
} from '../../services/cartService';

export type ProductCardCartSeed = {
  type: CartItem['type'];
  itemId: string;
  itemName: string;
  itemPrice: number;
  taxeHebergementEnabled?: boolean;
};

/**
 * Shared like / favorite / cart wiring for glass product cards.
 */
export function useProductCardCartActions(
  seed: ProductCardCartSeed,
  t: (key: string) => string
) {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => getCartItems());

  useEffect(() => {
    const load = () => setCartItems(getCartItems());
    load();
    const handler = () => load();
    window.addEventListener('cartUpdated', handler as EventListener);
    return () => window.removeEventListener('cartUpdated', handler as EventListener);
  }, []);

  const cartItem =
    cartItems.find((i) => i.type === seed.type && i.itemId === seed.itemId) || null;
  const isLiked = cartItem?.isLiked ?? false;
  const isFavorite = cartItem?.isFavorite ?? false;
  const isInCart = Boolean(cartItem);

  const ensureCartItem = useCallback(() => {
    if (cartItem) return cartItem;
    const created = addToCart({
      type: seed.type,
      itemId: seed.itemId,
      itemName: seed.itemName,
      itemPrice: seed.itemPrice,
      ...(seed.type === 'accommodation'
        ? { taxeHebergementEnabled: seed.taxeHebergementEnabled !== false }
        : {}),
    });
    setCartItems(getCartItems());
    return created;
  }, [cartItem, seed]);

  const onLike = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const item = ensureCartItem();
      toggleLike(item.id);
      setCartItems(getCartItems());
    },
    [ensureCartItem]
  );

  const onFavorite = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const item = ensureCartItem();
      toggleFavorite(item.id);
      setCartItems(getCartItems());
    },
    [ensureCartItem]
  );

  const onCart = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (cartItem) {
        removeFromCart(cartItem.id);
        setCartItems(getCartItems());
        return;
      }
      ensureCartItem();
    },
    [cartItem, ensureCartItem]
  );

  return {
    isLiked,
    isFavorite,
    isInCart,
    onLike,
    onFavorite,
    onCart,
    likeTitle: isLiked ? t('common.unlike') || 'Unlike' : t('common.like') || 'Like',
    favoriteTitle: isFavorite
      ? t('common.removeFromFavorites') || 'Remove from favorites'
      : t('common.addToFavorites') || 'Add to favorites',
    cartTitle: isInCart
      ? t('common.removeFromCart') || 'Remove from cart'
      : t('common.addToCart') || 'Add to cart',
  };
}
